import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { Component, input, OnChanges } from '@angular/core';

import { translate } from '@jsverse/transloco';

import { SentimentPolarityRange } from 'app/providers/storage-api/project.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SentimentUtils } from 'app/utils/sentiment.utils';
import { Utils } from 'app/utils/utils';

export enum SENTIMENT_ICON_5_SCALE {
  'very positive' = 'sentiment_very_positive',
  'somewhat positive' = 'sentiment_positive',
  'neutral' = 'sentiment_neutral',
  'somewhat negative' = 'sentiment_negative',
  'very negative' = 'sentiment_very_negative',
}

export enum SENTIMENT_ICON_3_SCALE {
  'positive' = 'sentiment_positive',
  'neutral' = 'sentiment_neutral',
  'negative' = 'sentiment_negative',
}

export enum SENTIMENT_COLOR_5_SCALE {
  'very positive' = 'very_positive',
  'somewhat positive' = 'positive',
  'neutral' = 'neutral',
  'somewhat negative' = 'negative',
  'very negative' = 'very_negative',
}

export enum SENTIMENT_COLOR_3_SCALE {
  'positive' = 'positive',
  'neutral' = 'neutral',
  'negative' = 'negative',
}

export enum SENTIMENT_HEX_COLOR_5_SCALE {
  'very positive' = '#009E0F',
  'somewhat positive' = '#6B8C5B',
  'neutral' = '#cccccc',
  'somewhat negative' = '#B16B6B',
  'very negative' = '#FF0000',
}

export enum SENTIMENT_HEX_COLOR_3_SCALE {
  'positive' = '#009E0F',
  'neutral' = '#cccccc',
  'negative' = '#FF0000',
}

export enum SentimentColor {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
  NEUTRAL = 'neutral',
  DEFAULT = 'orca',
}

export const NUMERIC_DEFAULT_COLOR = '#7CB5EC';

@EnableClassTelemetry()
@Component({
  selector: 'app-sentiment-display',
  templateUrl: './sentiment-display.component.html',
  styleUrls: ['./sentiment-display.component.scss'],
  providers: [TitleCasePipe, DecimalPipe],
  standalone: false,
})
export class SentimentDisplayComponent implements OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly sentimentScale = input<number>();
  readonly sentimentDisplay = input<string>();
  readonly precision = input<number>(1);
  readonly document = input<any>();
  readonly item = input<any>();
  readonly sentiment = input<any>();
  readonly sentimentRanges = input<SentimentPolarityRange[]>();
  readonly isTableView = input<boolean>();
  readonly showTooltip = input<boolean>(true);
  sentimentTooltip: string;

  constructor(
    private titleCasePipe: TitleCasePipe,
    private decimalPipe: DecimalPipe
  ) {}

  ngOnChanges() {
    this.sentimentTooltip = this.getSentimentTooltip();
  }

  get iconForPolarity() {
    switch (this.sentimentValue) {
      case 'very positive':
      case 'positive':
        return 'sentiment_very_satisfied';
      case 'somewhat positive':
        return 'sentiment_satisfied_alt';
      case 'neutral':
        return 'sentiment_dissatisfied';
      case 'somewhat negative':
        return 'sentiment_very_dissatisfied';
      case 'very negative':
      case 'negative':
        return 'mood_bad';
      default:
        return '';
    }
  }

  get colorForPolarity() {
    if (this.sentimentScale() === 5) {
      return (SENTIMENT_HEX_COLOR_5_SCALE[this.sentimentValue] ?? NUMERIC_DEFAULT_COLOR).substring(
        1
      );
    }

    return (SENTIMENT_HEX_COLOR_3_SCALE[this.sentimentValue] ?? NUMERIC_DEFAULT_COLOR).substring(1);
  }

  get sentimentIcon() {
    if (this.sentimentScale() === 5) {
      return SENTIMENT_ICON_5_SCALE[this.sentimentValue];
    }

    return SENTIMENT_ICON_3_SCALE[this.sentimentValue];
  }

  get sentimentColor() {
    if (this.sentimentScale() === 5) {
      return SENTIMENT_COLOR_5_SCALE[this.sentimentValue] ?? SentimentColor.DEFAULT;
    }

    return SENTIMENT_COLOR_3_SCALE[this.sentimentValue] ?? SentimentColor.DEFAULT;
  }

  get sentimentScore() {
    if (this.document()) {
      return this.document().analyses.length
        ? this.document().analyses[0].sentimentScore
        : undefined;
    } else if (this.item()) {
      return this.item().sentimentScore;
    } else {
      return this.sentiment().weight;
    }
  }

  get sentimentScoreOriginal() {
    if (this.document()) {
      return this.document().analyses.length
        ? this.document().analyses[0].sentimentScoreOriginal
        : undefined;
    } else if (this.item()) {
      return this.item().sentimentScoreOriginal;
    }
  }

  get sentimentValue() {
    return this.getSentimentValueFromRange(this.sentimentScore);
  }

  get sentimentValueOriginal() {
    return this.getSentimentValueFromRange(this.sentimentScoreOriginal);
  }

  get isModified(): boolean {
    return this.sentimentScoreOriginal != null;
  }

  private getRoundedValue(score) {
    return Utils.roundHalfAway(score, this.precision());
  }

  private getSentimentValueFromRange(score) {
    if (score == null) {
      return score;
    }

    const roundedScore = this.getRoundedValue(score);

    return SentimentUtils.getSentimentValueFromRange(
      roundedScore,
      this.sentimentRanges(),
      this.sentimentScale()
    );
  }

  getSentimentTooltip() {
    let tooltip = this.titleCasePipe.transform(this.sentimentValue);
    if (this.sentimentDisplay() === 'polarity') {
      tooltip += `: ${this.decimalPipe.transform(
        this.getRoundedValue(this.sentimentScore),
        '1.' + this.precision() + '-' + this.precision()
      )}`;
    }
    if (this.isModified) {
      tooltip += `
      (${translate('documents.sentiment.sentiment-was', {
        originalValue: this.titleCasePipe.transform(this.sentimentValueOriginal),
      })} ${this.decimalPipe.transform(
        this.getRoundedValue(this.sentimentScoreOriginal),
        '1.' + this.precision() + '-' + this.precision()
      )})`;
    }
    return tooltip;
  }
}
